/**
 * QR code tracking — data-layer client (feature #170).
 *
 * Thin typed wrapper over `apiClient` for the five endpoints backing the
 * QR tracking page:
 *   - POST   /api/v1/qr-tracking                       — mint a new QR
 *   - GET    /api/v1/qr-tracking/affiliate/:id         — list per affiliate
 *   - GET    /api/v1/qr-tracking/:slug/svg             — render SVG (text/svg)
 *   - POST   /api/v1/qr-tracking/:slug/scan            — record a scan
 *   - DELETE /api/v1/qr-tracking/:id                   — drop a QR row
 *
 * Mirrors the server-side view shape in apps/api/src/routes/qr-tracking.ts.
 * Keep this file dependency-free beyond `apiClient` + `apiBaseUrl` so pure
 * helpers (url validation, formatting, summaries) stay trivially testable.
 */

import { apiBaseUrl, apiClient, tenantId } from "../../api-client";

// ---------------------------------------------------------------------------
// Types — mirrors the backend view shape + create response.
// ---------------------------------------------------------------------------

export type QrTrackingStatus = "active" | "disabled";

export const QR_TRACKING_STATUSES: readonly QrTrackingStatus[] = [
	"active",
	"disabled",
] as const;

export function isQrTrackingStatus(v: unknown): v is QrTrackingStatus {
	return v === "active" || v === "disabled";
}

export interface QrTrackingRow {
	id: string;
	affiliate_id: string;
	slug: string;
	label: string;
	target_url: string;
	scan_count: number;
	last_scan_at: number | null;
	status: QrTrackingStatus;
	created_at: number;
	updated_at: number;
}

export interface QrTrackingCreateResponse extends QrTrackingRow {
	short_url: string;
}

export interface QrTrackingListResponse {
	affiliate_id: string;
	codes: QrTrackingRow[];
}

export interface QrTrackingScanResponse {
	ok: true;
	slug: string;
	target_url: string;
	scan_count: number;
	last_scan_at: number | null;
}

// ---------------------------------------------------------------------------
// Validators — the server enforces the same bounds; mirror them here so we
// can surface feedback before the round trip.
// ---------------------------------------------------------------------------

export const QR_LABEL_MAX_BYTES = 120;
export const QR_TARGET_URL_MAX_BYTES = 2048;

const AFFILIATE_ID_RE = /^[A-Za-z0-9_.:-]{1,128}$/;
const ID_RE = /^[A-Za-z0-9_.:-]{1,128}$/;

function byteLength(str: string): number {
	return new TextEncoder().encode(str).length;
}

export function isValidAffiliateId(v: unknown): v is string {
	return typeof v === "string" && AFFILIATE_ID_RE.test(v);
}

export function isValidId(v: unknown): v is string {
	return typeof v === "string" && ID_RE.test(v);
}

export function isValidLabel(v: unknown): v is string {
	if (v === undefined || v === null || v === "") return true;
	if (typeof v !== "string") return false;
	return byteLength(v.trim()) <= QR_LABEL_MAX_BYTES;
}

export function isValidTargetUrl(v: unknown): v is string {
	if (typeof v !== "string") return false;
	const trimmed = v.trim();
	if (trimmed.length === 0) return false;
	if (byteLength(trimmed) > QR_TARGET_URL_MAX_BYTES) return false;
	let parsed: URL;
	try {
		parsed = new URL(trimmed);
	} catch {
		return false;
	}
	return parsed.protocol === "http:" || parsed.protocol === "https:";
}

// ---------------------------------------------------------------------------
// Remote bindings — called directly from the page / mutations.
// ---------------------------------------------------------------------------

const BASE = "/qr-tracking";

export interface CreateQrArgs {
	affiliateId: string;
	label?: string;
	targetUrl: string;
	origin?: string;
}

export async function createQrCode(
	args: CreateQrArgs,
): Promise<QrTrackingCreateResponse> {
	return apiClient.post<QrTrackingCreateResponse>(BASE, {
		affiliateId: args.affiliateId,
		label: args.label ?? "",
		targetUrl: args.targetUrl,
		...(args.origin ? { origin: args.origin } : {}),
	});
}

export async function fetchQrCodesForAffiliate(
	affiliateId: string,
): Promise<QrTrackingListResponse> {
	return apiClient.get<QrTrackingListResponse>(
		`${BASE}/affiliate/${encodeURIComponent(affiliateId)}`,
	);
}

export async function recordQrScan(slug: string): Promise<QrTrackingScanResponse> {
	return apiClient.post<QrTrackingScanResponse>(
		`${BASE}/${encodeURIComponent(slug)}/scan`,
	);
}

export async function deleteQrCode(id: string): Promise<{ ok: true; id: string }> {
	return apiClient.delete<{ ok: true; id: string }>(
		`${BASE}/${encodeURIComponent(id)}`,
	);
}

/**
 * Build the SVG URL for a QR slug. The page uses this as the `src` attribute
 * on an `<img>` tag so the browser fetches (and caches) the SVG directly.
 *
 * The API requires the `x-tenant-id` header so we append the tenant as a
 * `tenant` query parameter fallback and let the browser fetch include it via
 * the image-loading path. The server accepts either; tests verify the query
 * form round-trips the slug + scale.
 */
export function buildQrSvgUrl(slug: string, scale = 8): string {
	const safeSlug = encodeURIComponent(slug);
	const safeScale = Math.max(2, Math.min(16, Math.trunc(scale) || 8));
	const url = new URL(`${apiBaseUrl}${BASE}/${safeSlug}/svg`);
	url.searchParams.set("scale", String(safeScale));
	url.searchParams.set("tenant", tenantId);
	return url.toString();
}

// ---------------------------------------------------------------------------
// Pure helpers — list summary, formatters, short-url preview.
// ---------------------------------------------------------------------------

export interface QrTrackingSummary {
	total: number;
	active: number;
	disabled: number;
	total_scans: number;
	last_scan_at: number | null;
}

/**
 * Collapses a list of QR rows into a single summary row. Deterministic:
 * `last_scan_at` is the max across the input; unused (never-scanned) codes
 * don't contribute. Pure — used for the stats row above the table.
 */
export function computeQrSummary(
	rows: readonly QrTrackingRow[],
): QrTrackingSummary {
	let active = 0;
	let disabled = 0;
	let totalScans = 0;
	let lastScanAt: number | null = null;
	for (const r of rows) {
		if (r.status === "active") active += 1;
		else if (r.status === "disabled") disabled += 1;
		totalScans += Number.isFinite(r.scan_count) ? r.scan_count : 0;
		if (
			typeof r.last_scan_at === "number" &&
			Number.isFinite(r.last_scan_at) &&
			r.last_scan_at > 0
		) {
			if (lastScanAt === null || r.last_scan_at > lastScanAt) {
				lastScanAt = r.last_scan_at;
			}
		}
	}
	return {
		total: rows.length,
		active,
		disabled,
		total_scans: totalScans,
		last_scan_at: lastScanAt,
	};
}

/**
 * Sort a list of QR rows for stable display: newest first by created_at,
 * then slug ascending for determinism. Never mutates the input.
 */
export function sortQrRows(rows: readonly QrTrackingRow[]): QrTrackingRow[] {
	return [...rows].sort((a, b) => {
		if (b.created_at !== a.created_at) return b.created_at - a.created_at;
		if (a.slug < b.slug) return -1;
		if (a.slug > b.slug) return 1;
		return 0;
	});
}

/**
 * Build a display-only short URL preview from an origin + slug. Used in the
 * table cell so operators can copy the URL before scanning.
 */
export function buildShortUrlPreview(origin: string, slug: string): string {
	const trimmed = (origin ?? "").trim().replace(/\/+$/, "");
	const safeSlug = encodeURIComponent(slug);
	if (!trimmed) return `/q/${safeSlug}`;
	return `${trimmed}/q/${safeSlug}`;
}

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export function formatScanTimestamp(unixSec: number | null | undefined): string {
	if (
		unixSec === null ||
		unixSec === undefined ||
		!Number.isFinite(unixSec) ||
		unixSec <= 0
	) {
		return "Never";
	}
	const d = new Date(unixSec * 1000);
	return d.toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function formatStatusLabel(status: QrTrackingStatus): string {
	return status === "active" ? "Active" : "Disabled";
}

export const qrTrackingQueryKeys = {
	all: ["qr-tracking"] as const,
	byAffiliate: (affiliateId: string) =>
		[...qrTrackingQueryKeys.all, "affiliate", affiliateId] as const,
};
